import React, { useState, useEffect } from "react";
import "./Agreements.css";
import AddAgreement from "./AddAgreement/AddAgreement";
import AgreementDetail from "./AgreementDetail/AgreementDetail";
import { getAgreements, deleteAgreement } from "../../../../api/agreementsApi";
import { getProjectEstimate } from "../../../../api/projectEstimatesApi";
import { getAllCounterparties } from "../../../../api/api";

/** Статусы оплаты по макету */
const PAYMENT_STATUS_LABELS = {
  paid_advance: "Оплачен Аванс",
  overdue_advance: "Просрочен Аванс",
  expected_ODGE: "Ожидается ОДГЭ",
  expected_OPGE: "Ожидается ОПГЭ",
  overdue_OPGE: "Просрочен ОПГЭ",
  // общие из API
  active: "Активно",
  draft: "Черновик",
  closed: "Закрыто",
};

const getCounterpartyDisplayName = (counterparty) =>
  counterparty?.full_name ||
  counterparty?.full_company_name ||
  counterparty?.short_company_name ||
  counterparty?.short_name ||
  (counterparty?.id != null ? `Контрагент #${counterparty.id}` : "");

const Agreements = ({ projectId }) => {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [apiPage, setApiPage] = useState(1);
  const pageSize = 20;

  const [searchQuery, setSearchQuery] = useState("");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [isAddAgreementOpen, setIsAddAgreementOpen] = useState(false);
  const [sectionNameById, setSectionNameById] = useState({});
  const [counterpartyNameById, setCounterpartyNameById] = useState({});
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setAgreements([]);
      setTotalCount(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAgreements({ project_id: Number(projectId), page: apiPage, page_size: pageSize })
      .then((data) => {
        if (!cancelled) {
          setAgreements(Array.isArray(data.results) ? data.results : []);
          setTotalCount(data.count ?? 0);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || "Не удалось загрузить соглашения");
          setAgreements([]);
          setTotalCount(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId, apiPage]);

  useEffect(() => {
    if (!projectId) {
      setSectionNameById({});
      return;
    }
    let cancelled = false;
    getProjectEstimate(Number(projectId))
      .then((estimate) => {
        if (cancelled) return;
        const map = {};
        const addSection = (item) => {
          const sectionId = item?.section?.id;
          const sectionName = item?.section?.name;
          if (sectionId != null && sectionName) {
            map[Number(sectionId)] = sectionName;
          }
        };
        (estimate?.contract_sections?.sections || []).forEach(addSection);
        (estimate?.construction_design || []).forEach(addSection);
        (estimate?.construction_design_project || []).forEach(addSection);
        setSectionNameById(map);
      })
      .catch(() => {
        if (!cancelled) setSectionNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    getAllCounterparties({ counterparty_side: "counterparty" })
      .then((items) => {
        if (cancelled) return;
        const list = Array.isArray(items) ? items : [];
        const map = {};
        list.forEach((item) => {
          if (item?.id != null) {
            map[Number(item.id)] = getCounterpartyDisplayName(item);
          }
        });
        setCounterpartyNameById(map);
      })
      .catch(() => {
        if (!cancelled) setCounterpartyNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshAgreements = () => {
    if (!projectId) return;
    getAgreements({ project_id: Number(projectId), page: apiPage, page_size: pageSize })
      .then((data) => {
        setAgreements(Array.isArray(data.results) ? data.results : []);
        setTotalCount(data.count ?? 0);
      })
      .catch((err) => {
        setError(err.message || "Не удалось загрузить соглашения");
      });
  };

  const toggleRowExpansion = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getAgreementPerformerName = (agreement) => {
    const counterpartySummary = agreement?.counterparty_summary;
    const performerNames = Array.isArray(agreement?.performer_names)
      ? agreement.performer_names.filter(Boolean)
      : [];
    return (
      counterpartySummary?.display_name ||
      counterpartySummary?.full_name ||
      counterpartySummary?.full_company_name ||
      counterpartySummary?.short_company_name ||
      agreement?.counterparty_name ||
      agreement?.company_name ||
      (agreement?.counterparty_id != null
        ? counterpartyNameById[Number(agreement.counterparty_id)]
        : null) ||
      (performerNames.length > 0 ? performerNames.join(", ") : null) ||
      (agreement?.counterparty_id != null
        ? `Контрагент #${agreement.counterparty_id}`
        : agreement?.company_id != null
          ? `Компания #${agreement.company_id}`
          : `№${agreement?.number ?? "—"}`)
    );
  };

  const getAgreementSectionsLabels = (agreement) => {
    const sectionIds = Array.isArray(agreement?.section_ids) ? agreement.section_ids : [];
    if (sectionIds.length > 0) {
      return sectionIds.map((sectionId) => sectionNameById[Number(sectionId)] || `Раздел #${sectionId}`);
    }
    const blockDetails = Array.isArray(agreement?.agreement_blocks_detail)
      ? agreement.agreement_blocks_detail
      : [];
    if (blockDetails.length > 0) {
      return blockDetails
        .map((b) => b?.block_name)
        .filter(Boolean);
    }
    const blockIds = Array.isArray(agreement?.block_ids) ? agreement.block_ids : [];
    return blockIds.map((blockId) => `Блок #${blockId}`);
  };

  const filteredAgreements = agreements.filter(
    (agreement) =>
      String(agreement.number ?? "").includes(searchQuery) ||
      String(agreement.company_id ?? "").includes(searchQuery) ||
      String(agreement.counterparty_id ?? "").includes(searchQuery) ||
      getAgreementPerformerName(agreement).toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(agreement.id ?? "").includes(searchQuery)
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const displayAgreements = filteredAgreements;

  const getPaymentStatusBadgeClass = (status) => {
    if (!status) return "status-badge";
    if (status.includes("paid") && !status.includes("overdue")) return "status-badge status-badge--paid";
    if (status.includes("overdue")) return "status-badge status-badge--overdue";
    if (status.includes("expected")) return "status-badge status-badge--expected";
    if (status === "draft" || status === "closed") return "status-badge status-badge--inactive";
    return "status-badge status-badge--expected";
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ru-RU").format(amount);
  };

  const handleDownloadAgreementDocument = (agreement, e) => {
    e.stopPropagation();
    if (!agreement?.document_url) return;
    window.open(agreement.document_url, "_blank", "noopener,noreferrer");
  };

  const handleDeleteClick = (agreementId, e) => {
    e.stopPropagation();
    setPendingDeleteId(agreementId);
  };

  const cancelDelete = () => {
    if (!isDeleting) setPendingDeleteId(null);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    setIsDeleting(true);
    try {
      await deleteAgreement(pendingDeleteId);
      setPendingDeleteId(null);
      setExpandedRows((prev) => {
        const next = new Set(prev);
        next.delete(pendingDeleteId);
        return next;
      });
      refreshAgreements();
    } catch (err) {
      setError(err.message || "Не удалось удалить соглашение");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="agreements-container">
      <div className="agreements-header">
        <h2 className="agreements-title">Субподрядчики проекта</h2>
      </div>

      <div className="agreements-controls">
        <div className="agreements-search">
          <input
            type="text"
            placeholder="Поиск по номеру, проекту, субподрядчику..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="agreements-search-input"
          />
        </div>
        <div className="agreements-filters">
          <select className="agreements-filter-select">
            <option>Все типы</option>
          </select>
          <select className="agreements-filter-select">
            <option>Все статусы</option>
          </select>
          <select className="agreements-filter-select">
            <option>Все разделы</option>
          </select>
        </div>
        <button
          className="agreements-add-btn"
          onClick={() => setIsAddAgreementOpen(true)}
        >
          <span>+</span> Добавить соглашение
        </button>
      </div>

      {error && (
        <div className="agreements-error" role="alert">
          {error}
        </div>
      )}

      {loading && (
        <div className="agreements-loading">Загрузка соглашений...</div>
      )}

      <div className="agreements-table-container">
        <table className="agreements-table">
          <thead>
            <tr>
              <th>Субподрядчик</th>
              <th>Разделы</th>
              <th>Сумма договора</th>
              <th>Этап</th>
              <th>Статус оплаты</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {!loading &&
              displayAgreements.map((agreement) => (
              <React.Fragment key={agreement.id}>
                <tr
                  className={`agreements-row ${
                    expandedRows.has(agreement.id) ? "expanded" : ""
                  }`}
                  onClick={() => toggleRowExpansion(agreement.id)}
                >
                  <td>
                    <div className="agreement-subcontractor">
                      <span className="expand-arrow">
                        {expandedRows.has(agreement.id) ? "▼" : "▶"}
                      </span>
                      <span className="subcontractor-name">
                        {getAgreementPerformerName(agreement)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="agreement-sections">
                      {getAgreementSectionsLabels(agreement).length > 0 ? (
                        getAgreementSectionsLabels(agreement).map((label, idx) => (
                          <span key={idx} className="section-tag">
                            {label}
                          </span>
                        ))
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                  </td>
                  <td className="agreement-amount">
                    {formatCurrency(agreement.price ?? 0)} ₸
                  </td>
                  <td className="agreement-stage">
                    {agreement.stage_name != null ? agreement.stage_name : "—"}
                  </td>
                  <td>
                    <span
                      className={getPaymentStatusBadgeClass(agreement.status)}
                    >
                      {PAYMENT_STATUS_LABELS[agreement.status] ?? agreement.status ?? "—"}
                    </span>
                  </td>
                  <td>
                    <div className="agreement-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="action-btn view-btn"
                        title={agreement.document_url ? "Скачать документ" : "Документ недоступен"}
                        onClick={(e) => handleDownloadAgreementDocument(agreement, e)}
                        disabled={!agreement.document_url}
                      >
                        📄
                      </button>
                      <button className="action-btn edit-btn" title="Редактировать">
                        ✎
                      </button>
                      <button
                        type="button"
                        className="action-btn delete-btn"
                        title="Удалить"
                        onClick={(e) => handleDeleteClick(agreement.id, e)}
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedRows.has(agreement.id) && (
                  <tr className="agreement-details-row">
                    <td colSpan="6">
                      <AgreementDetail
                        agreementId={agreement.id}
                        inline
                        fallbackCounterpartyName={getAgreementPerformerName(agreement)}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="agreements-pagination">
        <button
          type="button"
          className="pagination-btn"
          onClick={() => setApiPage((p) => Math.max(1, p - 1))}
          disabled={apiPage <= 1 || loading}
        >
          ‹
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            type="button"
            className={`pagination-btn ${page === apiPage ? "active" : ""}`}
            onClick={() => setApiPage(page)}
            disabled={loading}
          >
            {page}
          </button>
        ))}
        <button
          type="button"
          className="pagination-btn"
          onClick={() => setApiPage((p) => Math.min(totalPages, p + 1))}
          disabled={apiPage >= totalPages || loading}
        >
          ›
        </button>
      </div>

      <AddAgreement
        open={isAddAgreementOpen}
        onClose={() => {
          refreshAgreements();
          setIsAddAgreementOpen(false);
        }}
        projectId={projectId}
      />

      {pendingDeleteId != null && (
        <div className="agreements-popup-overlay" onClick={cancelDelete}>
          <div
            className="agreements-popup agreements-popup--confirm"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="agreements-delete-confirm-title"
          >
            <p id="agreements-delete-confirm-title" className="agreements-popup-text">
              Вы уверены что хотите удалить соглашение?
            </p>
            <div className="agreements-popup-buttons">
              <button
                type="button"
                className="agreements-popup-btn agreements-popup-btn--cancel"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="agreements-popup-btn agreements-popup-btn--confirm"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Удаление..." : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Agreements;
